import { FC, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

interface HeaderWithTooltipProps {
  title: string;
  subtitle?: string;
  tooltip: string;
}

/**
 * Table header component with optional subtitle and info tooltip
 * Uses portal to render tooltip above table overflow constraints
 */
export const HeaderWithTooltip: FC<HeaderWithTooltipProps> = ({
  title,
  subtitle,
  tooltip,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const iconRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
      });
    }
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5">
        <span className="font-medium text-gray-900">{title}</span>
        <div
          ref={iconRef}
          className="relative inline-flex"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Info className="w-3.5 h-3.5 text-gray-400 hover:text-blue-600 cursor-help transition-colors" />
        </div>
      </div>
      {subtitle && (
        <span className="text-xs text-gray-400 font-normal">{subtitle}</span>
      )}

      {/* Portal-based tooltip to escape overflow constraints */}
      {showTooltip && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            transform: 'translateX(-50%)',
          }}
        >
          {/* Arrow */}
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-900 rotate-45" />
          {/* Content */}
          <div className="relative bg-gray-900 text-white text-xs rounded-lg shadow-xl px-3 py-2.5 max-w-xs">
            <p className="leading-relaxed">{tooltip}</p>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
